import { useEffect, type ReactNode } from 'react';
import { SETUP_LAST_STEP, type Tenant } from '@alexiarest/shared';
import { useAuth } from '../auth/AuthContext';
import { IconoCheck } from '../components/icons';
import { ThemeToggle } from '../components/ThemeToggle';

/**
 * Marco del cuestionario inicial (carril, lista de pasos, icono de cada
 * paso) y la regla de cuando toca. Va en un modulo aparte y LIGERO porque
 * lo importan App.tsx (la guarda) y el alta: si viviera en SetupWizardPage,
 * el wizard entero, con el conector de TPV y la ficha fiscal, entraria en
 * el paquete inicial de todos los usuarios y la carga perezosa no serviria.
 */

export const SETUP_STEPS: Array<{ step: number; label: string; detail: string }> = [
  { step: 1, label: 'Tu restaurante', detail: 'Nombre, país y moneda' },
  { step: 2, label: 'Facturación', detail: 'A quién le facturamos' },
  { step: 3, label: 'Cómo vendes', detail: 'Impuesto, comisiones y delivery' },
  { step: 4, label: 'Tu TPV', detail: 'De dónde entran las ventas' },
  { step: 5, label: 'Objetivos', detail: 'A qué quieres llegar' },
];


/** Iconos de cada paso, en linea para no depender de nada. */
export function IconoPaso({ step }: { step: number }): JSX.Element {
  const comun = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (step) {
    case 1:
      return (
        <svg {...comun}>
          <path d="M3 10l1.5-5h15L21 10" />
          <path d="M4 10v10h16V10" />
          <path d="M9 20v-6h6v6" />
        </svg>
      );
    case 2:
      return (
        <svg {...comun}>
          <path d="M6 3h9l4 4v14H6z" />
          <path d="M15 3v4h4" />
          <path d="M9 12h7M9 16h7" />
        </svg>
      );
    case 3:
      return (
        <svg {...comun}>
          <path d="M20 12l-8 8-9-9V4h7z" />
          <circle cx="7.5" cy="7.5" r="1.3" />
        </svg>
      );
    case 4:
      return (
        <svg {...comun}>
          <rect x="4" y="3" width="16" height="13" rx="2" />
          <path d="M8 21h8M12 16v5" />
        </svg>
      );
    default:
      return (
        <svg {...comun}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1.2" />
        </svg>
      );
  }
}

/** ¿Le toca a este usuario el cuestionario? Solo al propietario y solo si está abierto. */
export function setupPending(tenant: Tenant | undefined, role: string | undefined): boolean {
  if (!tenant?.setup || tenant.setup.completedAt) return false;
  return role === 'owner' || role === 'admin';
}

/** Lista de pasos del carril: los hechos, el actual y los que quedan. */
export function SetupProgress({ current }: { current: number }): JSX.Element {
  return (
    <ol className="setup__pasos" aria-label="Pasos de la configuración inicial">
      {SETUP_STEPS.map(({ step, label, detail }) => {
        const estado = step < current ? 'hecho' : step === current ? 'actual' : 'pendiente';
        return (
          <li
            key={step}
            className={`setup__paso setup__paso--${estado}`}
            aria-current={estado === 'actual' ? 'step' : undefined}
          >
            <span className="setup__paso-icono" aria-hidden="true">
              {estado === 'hecho' ? <IconoCheck /> : <IconoPaso step={step} />}
            </span>
            <span className="setup__paso-texto">
              <span className="setup__paso-label">{label}</span>
              <span className="setup__paso-detalle">{detail}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Marco comun del cuestionario: un carril verde a la izquierda con el logo
 * y la lista de pasos (en movil, una barra compacta con puntos), y el
 * contenido del paso a la derecha sobre el fondo crema de la app.
 */
export function SetupShell({
  current,
  children,
}: {
  current: number;
  children: ReactNode;
}): JSX.Element {
  const { logout } = useAuth();
  // Cada paso empieza arriba: el anterior se pudo dejar con la pagina al
  // fondo (el boton de continuar esta al final) y el nuevo salia cortado.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [current]);
  const actual = SETUP_STEPS.find((s) => s.step === current);
  // Tras el ultimo paso viene el resumen, que no es un paso mas.
  const etiqueta = current > SETUP_LAST_STEP ? 'Resumen' : `Paso ${current} de ${SETUP_LAST_STEP}`;
  return (
    <div className="setup">
      <aside className="setup__rail">
        <div className="setup__rail-top">
          <img
            className="setup__logo"
            src="/logo/logotipo-oscuro.svg"
            alt="Alexia Rest"
            width={217}
            height={48}
          />
          <p className="setup__eyebrow">Configuración inicial</p>
          <p className="setup__rail-claim">Cinco pasos y Alexia está lista para tu negocio.</p>
        </div>
        <SetupProgress current={current} />
        <div className="setup__rail-foot">
          <p className="setup__rail-nota">Todo lo que respondas se puede cambiar luego en Ajustes.</p>
          <button type="button" className="setup__salir" onClick={logout}>
            Salir
          </button>
        </div>
      </aside>

      <div className="setup__main">
        <header className="setup__movil">
          <img className="setup__logo-movil" src="/logo/logotipo-claro.svg" alt="Alexia Rest" width={217} height={48} />
          <div className="setup__movil-pasos">
            <span className="setup__movil-num">{etiqueta}</span>
            <span className="setup__movil-titulo">{actual?.label ?? 'Todo listo'}</span>
            <ol className="setup__puntos" aria-hidden="true">
              {SETUP_STEPS.map(({ step }) => (
                <li key={step} className={step < current ? 'is-hecho' : step === current ? 'is-actual' : ''} />
              ))}
            </ol>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={logout}>
            Salir
          </button>
        </header>
        <div className="setup__contenido">
          <div className="setup__barra">
            <span className="setup__eyebrow setup__eyebrow--tinta">{etiqueta}</span>
            <ThemeToggle />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

